import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getProjects,
  getProject,
  createProject,
  updateProject,
  deleteProject,
  duplicateProject,
  getProjectStats,
  compareProjects,
  getProjectTrends,
} from '../services/projectsAPI';

// 專案範本相關 API 已移除

// ==================== Query Keys ====================

export const projectKeys = {
  all: ['projects'],
  lists: () => [...projectKeys.all, 'list'],
  list: (filters) => [...projectKeys.lists(), filters],
  details: () => [...projectKeys.all, 'detail'],
  detail: (id) => [...projectKeys.details(), id],
  stats: (id) => [...projectKeys.detail(id), 'stats'],
  comparison: (ids) => [...projectKeys.all, 'comparison', ids],
  trends: (params) => [...projectKeys.all, 'trends', params],
  templates: () => [...projectKeys.all, 'templates'],
  templateList: (filters) => [...projectKeys.templates(), 'list', filters],
  template: (id) => [...projectKeys.templates(), 'detail', id],
};

// ==================== Queries ====================

/**
 * 獲取專案列表
 * @param {Object} filters - 篩選條件
 * @param {Object} options - React Query 選項
 */
export function useProjects(filters = {}, options = {}) {
  return useQuery({
    queryKey: projectKeys.list(filters),
    queryFn: () => getProjects(filters),
    staleTime: 5 * 60 * 1000, // 5 分鐘
    ...options,
  });
}

/**
 * 獲取單一專案詳情
 * @param {string} id - 專案 ID
 * @param {Object} queryOptions - 查詢選項
 * @param {Object} options - React Query 選項
 */
export function useProject(id, queryOptions = {}, options = {}) {
  return useQuery({
    queryKey: projectKeys.detail(id),
    queryFn: () => getProject(id, queryOptions),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
    ...options,
  });
}

/**
 * 獲取專案統計
 * @param {string} id - 專案 ID
 * @param {Object} options - React Query 選項
 */
export function useProjectStats(id, options = {}) {
  return useQuery({
    queryKey: projectKeys.stats(id),
    queryFn: () => getProjectStats(id),
    enabled: !!id,
    staleTime: 1 * 60 * 1000, // 1 分鐘
    ...options,
  });
}

/**
 * 比較多個專案
 * @param {string[]} projectIds - 專案 ID 陣列
 * @param {Object} params - 比較參數
 * @param {Object} options - React Query 選項
 */
export function useProjectComparison(projectIds, params = {}, options = {}) {
  return useQuery({
    queryKey: projectKeys.comparison(projectIds),
    queryFn: () =>
      compareProjects({
        project_ids: projectIds,
        ...params,
      }),
    enabled: projectIds && projectIds.length >= 2,
    staleTime: 5 * 60 * 1000,
    ...options,
  });
}

/**
 * 獲取專案趨勢
 * @param {Object} params - 趨勢參數
 * @param {Object} options - React Query 選項
 */
export function useProjectTrends(params, options = {}) {
  return useQuery({
    queryKey: projectKeys.trends(params),
    queryFn: () => getProjectTrends(params),
    enabled: !!(params?.start_year && params?.end_year),
    staleTime: 10 * 60 * 1000, // 10 分鐘
    ...options,
  });
}

// ==================== Mutations ====================

/**
 * 建立專案
 * @param {Object} options - Mutation 選項
 */
export function useCreateProject(options = {}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createProject,
    onSuccess: (data) => {
      // 使所有專案列表快取失效
      queryClient.invalidateQueries({ queryKey: projectKeys.lists() });
      options.onSuccess?.(data);
    },
    ...options,
  });
}

/**
 * 更新專案
 * @param {Object} options - Mutation 選項
 */
export function useUpdateProject(options = {}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }) => updateProject(id, data),
    onSuccess: (data, variables) => {
      // 更新專案詳情快取
      queryClient.setQueryData(projectKeys.detail(variables.id), data);

      // 使專案列表快取失效
      queryClient.invalidateQueries({ queryKey: projectKeys.lists() });

      options.onSuccess?.(data, variables);
    },
    ...options,
  });
}

/**
 * 刪除專案
 * @param {Object} options - Mutation 選項
 */
export function useDeleteProject(options = {}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, hard_delete }) => deleteProject(id, { hard_delete }),
    onSuccess: (data, variables) => {
      // 移除專案詳情快取
      queryClient.removeQueries({ queryKey: projectKeys.detail(variables.id) });

      // 使專案列表快取失效
      queryClient.invalidateQueries({ queryKey: projectKeys.lists() });

      options.onSuccess?.(data, variables);
    },
    ...options,
  });
}

/**
 * 複製專案
 * @param {Object} options - Mutation 選項
 */
export function useDuplicateProject(options = {}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }) => duplicateProject(id, data),
    onSuccess: (data) => {
      // 使專案列表快取失效
      queryClient.invalidateQueries({ queryKey: projectKeys.lists() });

      options.onSuccess?.(data);
    },
    ...options,
  });
}

/**
 * 刷新專案統計
 * @param {Object} options - Mutation 選項
 */
export function useRefreshProjectStats(options = {}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: getProjectStats,
    onSuccess: (data, id) => {
      // 更新專案統計快取
      queryClient.setQueryData(projectKeys.stats(id), data);

      // 使專案詳情快取失效（因為包含統計）
      queryClient.invalidateQueries({ queryKey: projectKeys.detail(id) });

      options.onSuccess?.(data, id);
    },
    ...options,
  });
}

// ==================== 輔助 Hooks ====================

/**
 * 使用樂觀更新的專案狀態變更
 * @param {string} projectId - 專案 ID
 */
export function useProjectStatusUpdate(projectId) {
  const queryClient = useQueryClient();
  const updateMutation = useUpdateProject();

  return useMutation({
    mutationFn: (newStatus) =>
      updateProject(projectId, { status: newStatus }),
    onMutate: async (newStatus) => {
      // 取消進行中的查詢
      await queryClient.cancelQueries({ queryKey: projectKeys.detail(projectId) });

      // 保存舊資料
      const previousProject = queryClient.getQueryData(
        projectKeys.detail(projectId),
      );

      // 樂觀更新
      if (previousProject) {
        queryClient.setQueryData(projectKeys.detail(projectId), {
          ...previousProject,
          data: {
            ...previousProject.data,
            status: newStatus,
          },
        });
      }

      return { previousProject };
    },
    onError: (err, newStatus, context) => {
      // 恢復舊資料
      if (context?.previousProject) {
        queryClient.setQueryData(
          projectKeys.detail(projectId),
          context.previousProject,
        );
      }
    },
    onSettled: () => {
      // 重新獲取資料
      queryClient.invalidateQueries({ queryKey: projectKeys.detail(projectId) });
      queryClient.invalidateQueries({ queryKey: projectKeys.lists() });
    },
  });
}

/**
 * 預取專案詳情
 */
export function usePrefetchProject() {
  const queryClient = useQueryClient();

  return (id) => {
    queryClient.prefetchQuery({
      queryKey: projectKeys.detail(id),
      queryFn: () => getProject(id),
      staleTime: 5 * 60 * 1000,
    });
  };
}

// ==================== 專案範本 Queries 已移除 ====================
// 專案模板功能已移除，使用 5 步驟精靈建立專案
